/**
 * Token Refresh Service
 * Handles automatic silent refresh of custom tokens before they expire
 * 
 * Features:
 * - Silent token refresh (no user interaction)
 * - Automatic refresh before token expires
 * - Works for both User Flow (MSAL) and App Flow (backend /login)
 * - Generates new custom token with unique sid on each refresh
 */

import Axios from 'axios';
import { API_URL, MS_URL } from './Constants';
import { PublicClientApplication } from '@azure/msal-browser';
import { loginRequest, msalConfig } from '../../authConfig';
import { decodeAccessToken, createCustomOidToken, getAzureTokenId } from './tokenUtils';
import { clearAuthStorage, safeGetLocalStorage, safeSetLocalStorage, safeSetSessionStorage } from './storageUtils';

const msalInstance = new PublicClientApplication(msalConfig);

class TokenRefreshService {
  constructor() {
    this.refreshTimer = null;
    this.isRefreshing = false;
    this.refreshIntervalMinutes = 55; // Refresh 5 minutes before 1-hour expiry
  }

  /**
   * Start automatic token refresh
   * Calculates refresh time based on custom token expiry in localStorage
   * 
   * Note: localStorage.accessToken contains the CUSTOM token (not Azure token)
   * The custom token has exp synced with Azure token expiry
   */
  startTokenRefresh() {
    // Clear any existing timer
    this.stopTokenRefresh();

    const customToken = localStorage.getItem('accessToken');
    
    if (!customToken) {
      return;
    }

    try {
      // Decode custom token to get expiry
      const decoded = decodeAccessToken(customToken);
      let expiresIn;
      
      if (decoded && decoded.exp) {
        const now = Math.floor(Date.now() / 1000);
        expiresIn = decoded.exp - now;
      } else {
        // Default expiry (Azure tokens typically expire in 1 hour)
        expiresIn = 3600;
      }

      // Calculate time until refresh (refresh 5 minutes before expiry)
      const refreshIn = Math.max((expiresIn - 300), 60); // Refresh 5 min before expiry, minimum 1 min

      this.scheduleRefresh(refreshIn * 1000); // Convert to milliseconds
    } catch (error) {
      // Fallback to default refresh interval
      this.scheduleRefresh(this.refreshIntervalMinutes * 60 * 1000);
    }
  }

  /**
   * Schedule refresh after specified delay
   * @param {number} delayMs - Delay in milliseconds
   */
  scheduleRefresh(delayMs) {
    this.refreshTimer = setTimeout(() => {
      this.silentRefresh();
    }, delayMs);
  }

  /**
   * Stop automatic token refresh
   */
  stopTokenRefresh() {
    if (this.refreshTimer) {
      clearTimeout(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Perform silent token refresh
   * Acquires new Azure token and generates new custom token with unique sid
   * 
   * SECURITY: Azure tokens are NOT stored in localStorage.
   * Only non-sensitive identifiers (oid, tid) and expiry metadata are cached.
   */
  async silentRefresh() {
    if (this.isRefreshing) {
      console.log('TokenRefreshService: Refresh already in progress, skipping');
      return;
    }

    this.isRefreshing = true;

    try {
      const clientId = localStorage.getItem('clientId');
      let userOid;
      let azureTokenExp;
      let azureTokenId;
      let isAppFlow = false;

      // ==================== APP FLOW ====================
      if (clientId) {
        isAppFlow = true;

        try {
          // Re-authenticate via backend /login endpoint to get new Azure token
          const response = await Axios.post(`${API_URL}/login?client_id=${clientId}`);
          
          if (response.data && response.data.accessToken) {
            // Decode to extract claims (DO NOT store the actual Azure token)
            const decoded = decodeAccessToken(response.data.accessToken);
            userOid = decoded?.oid;
            azureTokenExp = decoded?.exp;
            azureTokenId = getAzureTokenId(decoded);

            if (!userOid) {
              throw new Error('No OID found in app flow token');
            }

            // Store ONLY non-sensitive identifiers (NOT the Azure token)
            localStorage.setItem('userOid', userOid);
            if (decoded?.tid) {
              localStorage.setItem('userTid', decoded.tid);
            }
            
            // Cache expiry in sessionStorage (cleared on browser close)
            if (azureTokenExp) {
              sessionStorage.setItem('appFlowTokenExp', azureTokenExp.toString());
            }
            if (azureTokenId) {
              sessionStorage.setItem('appFlowTokenAti', azureTokenId);
            }
          } else {
            throw new Error('Failed to get new Azure token for app flow');
          }
        } catch (error) {
          throw error;
        }
      }
      // ==================== USER FLOW ====================
      else {
        try {
          // Get active MSAL account
          const activeAccount = msalInstance.getActiveAccount();
          const accounts = msalInstance.getAllAccounts();

          if (!activeAccount && accounts.length === 0) {
            throw new Error('No active MSAL account found');
          }

          const account = activeAccount || accounts[0];
          const request = {
            ...loginRequest,
            account: account,
          };

          // Acquire token silently via MSAL (MSAL manages its own cache)
          const authResult = await msalInstance.acquireTokenSilent(request);

          // Decode to extract claims (DO NOT store the actual Azure token)
          const decoded = decodeAccessToken(authResult.accessToken);
          userOid = decoded?.oid;
          azureTokenExp = decoded?.exp;
          azureTokenId = getAzureTokenId(decoded);

          if (!userOid) {
            throw new Error('No OID found in user flow token');
          }

          // Store ONLY non-sensitive identifiers (NOT the Azure token)
          localStorage.setItem('userOid', userOid);
          if (decoded?.tid) {
            localStorage.setItem('userTid', decoded.tid);
          }
        } catch (error) {
          // If silent refresh fails, try interactive login
          if (error.name === 'InteractionRequiredAuthError') {
            this.stopTokenRefresh();
            window.location.href = MS_URL;
            return;
          }
          
          throw error;
        }
      }

      // ==================== GENERATE NEW CUSTOM TOKEN ====================
      // Generate new custom token with Azure token expiry and unique ID
      const newCustomToken = createCustomOidToken({
        oid: userOid,
        azureTokenExp: azureTokenExp,
        azureTokenId: azureTokenId,
        isAppFlow: isAppFlow,
        clientId: isAppFlow ? clientId : null
      });

      // Store the new custom token in localStorage (replaces old one)
      localStorage.setItem('accessToken', newCustomToken);

      // Dispatch event to notify app components that token was refreshed
      window.dispatchEvent(new CustomEvent('token-refreshed', {
        detail: {
          isAppFlow: isAppFlow,
          timestamp: Date.now()
        }
      }));

      // Schedule next refresh
      this.startTokenRefresh();

    } catch (error) {
      // Stop refresh service and redirect to login
      this.stopTokenRefresh();
      
      // Clear storage
      clearAuthStorage();
      
      // Redirect to login
      window.location.href = MS_URL;
    } finally {
      this.isRefreshing = false;
    }
  }

  /**
   * Manually trigger token refresh
   * Useful for testing or forcing refresh
   * @returns {Promise<void>}
   */
  async manualRefresh() {
    await this.silentRefresh();
  }

  /**
   * Get service status
   * @returns {object} Service status information
   */
  getStatus() {
    return {
      isActive: this.refreshTimer !== null,
      isRefreshing: this.isRefreshing,
      refreshIntervalMinutes: this.refreshIntervalMinutes,
    };
  }
}

// Export singleton instance
export default new TokenRefreshService();

